import { useRouter } from "next/router";
import Script from "next/script";
import { createContext, useCallback, useMemo, useContext } from "react";
import { useEffect, useRef } from "react";

type RUMContext = {
  /**
   * Call this when a page is completely rendered to emit metrics. Definition of "complete" varies
   * by page.
   */
  sendMetrics: () => void;
};

const defaultRUMContext: RUMContext = {
  sendMetrics: () => undefined,
};

const RUMContext = createContext<RUMContext>(defaultRUMContext);

export const useRUMContext = (): RUMContext => {
  return useContext(RUMContext);
};

/**
 * Provides RUM integration with third-party service. Current implementation uses SpeedCurve's LUX.
 * The highl-level sequencing of events are as such:
 *
 * - Download and configure LUX.
 * - When a page is completely rendered(*), call LUX.send().
 * - (Optional) When the route's about to change, call LUX.init().
 * - (Optional) When the new page is completely rendered(*), call LUX.send().
 * - (Optional) Repeat the last two steps.
 *
 * (*) Completely rendered is different from page to page because a page can be static or dynamic.
 * The decision should be made by each individual page. See <Page /> for more info.
 *
 * Caveat: Landing on a dynamic page (e.g. /bills/:id) results in sending metrics for `/bills` _and_
 * `/bills/:id` because the way Next.js works. See links for more info:
 *
 * - https://github.com/vercel/next.js/issues/15082
 * - https://nextjs.org/docs/routing/dynamic-routes#caveats
 */
const ActiveRUMProvider: React.FC<unknown> = (props) => {
  const router = useRouter();

  // This is an internal flag that shouldn't trigger a re-render.
  const alreadyInitialized = useRef(false);

  // Call this to close the init-send loops. Only works if `init()` was called before.
  const sendLUX = useCallback(() => {
    LUX?.send();
    alreadyInitialized.current = false;
  }, []);

  useEffect(() => {
    const handleRouteChangeStart: Parameters<typeof router.events.on>[1] = () => {
      // Close previous/existing init-send loop.
      if (alreadyInitialized.current) {
        sendLUX();
      }

      // The doc (https://support.speedcurve.com/en/articles/867066-lux-api) says `init()` should be
      // called on the start of page transition while `send()` should be at the end. So we call
      // `init()` here and call `send()` when the page is mounted. See <Page /> for more.
      LUX?.init();
      alreadyInitialized.current = true;
    };

    // Can happen if there's an error or cancellation during route change.
    const handleRouteChangeError: Parameters<typeof router.events.on>[1] = () => {
      sendLUX();
    };

    router.events.on("routeChangeStart", handleRouteChangeStart);
    router.events.on("routeChangeError", handleRouteChangeError);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
      router.events.off("routeChangeError", handleRouteChangeError);
    };
  }, [router, sendLUX]);

  const value = useMemo(() => ({ sendMetrics: sendLUX }), [sendLUX]);

  return (
    <>
      <Script id="lux_definition">
        {`LUX=(function(){var a=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMarks)?LUX.gaMarks:[]);var d=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMeasures)?LUX.gaMeasures:[]);var j="LUX_start";var k=window.performance;var l=("undefined"!==typeof(LUX)&&LUX.ns?LUX.ns:(Date.now?Date.now():+(new Date())));if(k&&k.timing&&k.timing.navigationStart){l=k.timing.navigationStart}function f(){if(k&&k.now){return k.now()}var o=Date.now?Date.now():+(new Date());return o-l}function b(n){if(k){if(k.mark){return k.mark(n)}else{if(k.webkitMark){return k.webkitMark(n)}}}a.push({name:n,entryType:"mark",startTime:f(),duration:0});return}function m(p,t,n){if("undefined"===typeof(t)&&h(j)){t=j}if(k){if(k.measure){if(t){if(n){return k.measure(p,t,n)}else{return k.measure(p,t)}}else{return k.measure(p)}}else{if(k.webkitMeasure){return k.webkitMeasure(p,t,n)}}}var r=0,o=f();if(t){var s=h(t);if(s){r=s.startTime}else{if(k&&k.timing&&k.timing[t]){r=k.timing[t]-k.timing.navigationStart}else{return}}}if(n){var q=h(n);if(q){o=q.startTime}else{if(k&&k.timing&&k.timing[n]){o=k.timing[n]-k.timing.navigationStart}else{return}}}d.push({name:p,entryType:"measure",startTime:r,duration:(o-r)});return}function h(n){return c(n,g())}function c(p,o){for(i=o.length-1;i>=0;i--){var n=o[i];if(p===n.name){return n}}return undefined}function g(){if(k){if(k.getEntriesByType){return k.getEntriesByType("mark")}else{if(k.webkitGetEntriesByType){return k.webkitGetEntriesByType("mark")}}}return a}return{mark:b,measure:m,gaMarks:a,gaMeasures:d}})();LUX.ns=(Date.now?Date.now():+(new Date()));LUX.ac=[];LUX.cmd=function(a){LUX.ac.push(a)};LUX.init=function(){LUX.cmd(["init"])};LUX.send=function(){LUX.cmd(["send"])};LUX.addData=function(a,b){LUX.cmd(["addData",a,b])};LUX_ae=[];window.addEventListener("error",function(a){LUX_ae.push(a)});LUX_al=[];if("function"===typeof(PerformanceObserver)&&"function"===typeof(PerformanceLongTaskTiming)){var LongTaskObserver=new PerformanceObserver(function(c){var b=c.getEntries();for(var a=0;a<b.length;a++){var d=b[a];LUX_al.push(d)}});try{LongTaskObserver.observe({type:["longtask"]})}catch(e){}};`}
      </Script>

      <Script id="lux_set_debug">{`LUX.debug=${process.env.NEXT_PUBLIC_LUX_DEBUG};`}</Script>

      {/*
        Prevent the LUX beacon from being automatically sent. Because this is a SPA, the beacon will
        be sent manually by each page. More info:

        - https://support.speedcurve.com/en/articles/867066-lux-api#luxauto
        - https://github.com/SpeedCurve-Metrics/lux-examples/blob/master/react/public/index.html#L36
      */}
      <Script id="lux_set_auto">{`LUX.auto=false;`}</Script>

      <Script
        src="https://cdn.speedcurve.com/js/lux.js?id=3174896719"
        async
        defer
        crossOrigin="anonymous"
      />

      <RUMContext.Provider value={value}>{props.children}</RUMContext.Provider>
    </>
  );
};

/**
 * A stub that skips on everything when we don't want RUM (e.g. in staging and/or dev)
 */
const InactiveRUMProvider: React.FC<unknown> = (props) => {
  return <>{props.children}</>;
};

export const RUMProvider =
  process.env.NEXT_PUBLIC_LUX_ENABLED === "true" ? ActiveRUMProvider : InactiveRUMProvider;
